import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Stack,
  IconButton,
  TextField,
} from "@mui/material";
import StandardTable, { TableColumn } from "../common/StandardTable";
import ConfirmDialog from "../common/ConfirmDialog";
import { formatCurrency } from "../../utils/currencyUtils";
import {
  addReceiptItem,
  updateCartItemFields,
  removeReceiptItem,
  changeSaleType,
  resetReceipt,
} from "../../redux/actions/receiptActions";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import AppButton from "../buttons/AppButton";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { ItemResponseDto } from "../../shared/dtos/item/ItemResponseDto";
import { SaleType } from "../../shared/enums/SaleType";

interface PosDesktopViewProps {
  items: ItemResponseDto[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  pagination: { page: number; limit: number; totalElements?: number };
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  onOpenPreview: () => void;
}

const PosDesktopView: React.FC<PosDesktopViewProps> = ({
  items,
  loading,
  error,
  onRetry,
  pagination,
  onPageChange,
  onLimitChange,
  searchTerm,
  setSearchTerm,
  onOpenPreview,
}) => {
  const dispatch = useAppDispatch();

  const {
    cartItems,
    cartSubtotal: subtotal,
    saleType,
  } = useAppSelector((state) => state.receipt);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState<boolean>(false);

  const handleUpdateField = (
    itemId: string,
    field: string,
    value: string | number,
  ): void => {
    if (value === "" || Number(value) >= 0) {
      dispatch(updateCartItemFields(itemId, { [field]: value }));
    }
  };

  const columns: TableColumn<ItemResponseDto>[] = React.useMemo(
    () => [
      {
        id: "name",
        label: "NAME",
        minWidth: 150,
        format: (_, row) => <b>{row.name}</b>,
      },
      { id: "sku", label: "SKU", minWidth: 100 },
      { id: "brandName", label: "BRAND", minWidth: 100 },
      { id: "categoryName", label: "CATEGORY", minWidth: 100 },
      {
        id: "retailPrice",
        label: "RETAIL",
        minWidth: 100,
        format: (_, row) => (
          <Typography color="primary" sx={{ fontWeight: "bold" }}>
            {formatCurrency(row.retailPrice)}
          </Typography>
        ),
      },
      {
        id: "wholesalePrice",
        label: "WHOLESALE",
        minWidth: 100,
        format: (_, row) => (
          <Typography color="secondary" sx={{ fontWeight: "bold" }}>
            {formatCurrency(row.wholesalePrice)}
          </Typography>
        ),
      },
    ],
    [],
  );

  return (
    <Grid
      container
      spacing={2}
      sx={{ height: "calc(100vh - 120px)", overflow: "hidden" }}
    >
      {/* LEFT: PRODUCTS TABLE */}
      <Grid
        size={{ xs: 12, md: 9.0 }}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Point of Sale
          </Typography>
          <TextField
            size="small"
            placeholder="Search product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 300 }}
          />
        </Stack>

        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          {error ? (
            <Box sx={{ mt: 5, textAlign: "center" }}>
              <Typography color="error">{error}</Typography>
              <AppButton
                variant="contained"
                color={"error"}
                onClick={onRetry}
                sx={{ mt: 2 }}
                pill
              >
                Retry
              </AppButton>
            </Box>
          ) : (
            <StandardTable
              columns={columns}
              data={items}
              loading={loading}
              page={pagination.page}
              count={pagination?.totalElements || 0}
              rowsPerPage={pagination.limit}
              onPageChange={onPageChange}
              onRowsPerPageChange={onLimitChange}
              rowAction={(row) => (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => dispatch(addReceiptItem(row))}
                >
                  <AddCircleIcon />
                </IconButton>
              )}
            />
          )}
        </Box>
      </Grid>

      {/* RIGHT: CART PANE */}
      <Grid size={{ xs: 12, md: 3.0 }} sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "90%",
            py: 1.5,
            px: 1,
            bgcolor: "background.paper",
            borderRadius: 2,
          }}
        >
          {/* Header */}
          <Box sx={{ flexShrink: 0, mb: 2 }}>
            <Stack direction="row" sx={{ spacing: 1, mb: 2 }}>
              <AppButton
                fullWidth
                size="small"
                variant="contained"
                pill
                color={saleType === SaleType.RETAIL ? "primary" : "inherit"}
                onClick={() => dispatch(changeSaleType(SaleType.RETAIL))}
                sx={{ opacity: saleType === SaleType.RETAIL ? 1 : 0.4, py: 1 }}
              >
                Retail
              </AppButton>
              <AppButton
                fullWidth
                size="small"
                variant="contained"
                pill
                color={
                  saleType === SaleType.WHOLESALE ? "secondary" : "inherit"
                }
                onClick={() => dispatch(changeSaleType(SaleType.WHOLESALE))}
                sx={{
                  opacity: saleType === SaleType.WHOLESALE ? 1 : 0.4,
                  py: 1,
                }}
              >
                Wholesale
              </AppButton>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Cart Items ({cartItems.length})
            </Typography>
          </Box>

          {/* Cart Items List */}
          <Box sx={{ flexGrow: 1, overflowY: "auto", pr: 0.5 }}>
            <Stack spacing={1.5}>
              {cartItems.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.disabled"
                  sx={{ align: "center", mt: 4 }}
                >
                  Cart is empty. Add products to start.
                </Typography>
              ) : (
                cartItems.map((cItem) => (
                  <Box
                    key={cItem.itemId}
                    sx={{
                      p: 1.5,
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.03)",
                    }}
                  >
                    <Stack direction={"column"} spacing={1}>
                      <Stack
                        direction="row"
                        sx={{
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", pr: 1, mb: 0.5 }}
                        >
                          {cItem.name}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            dispatch(removeReceiptItem(cItem.itemId))
                          }
                          sx={{ mt: -0.5, mr: -0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Stack
                        direction="row"
                        sx={{
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {cItem.brand ? `${cItem.brand} | ` : ""}
                          {cItem.category || ""}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            color: "success.main",
                            whiteSpace: "nowrap",
                            ml: 1,
                          }}
                        >
                          {formatCurrency(
                            (Number(cItem.price) -
                              Number(cItem.discount || 0)) *
                              Number(cItem.qty),
                          )}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1.5}
                        sx={{ alignItems: "center" }}
                      >
                        <TextField
                          label="Price"
                          size="small"
                          type="number"
                          value={cItem.price}
                          onChange={(e) =>
                            handleUpdateField(
                              cItem.itemId,
                              "price",
                              e.target.value,
                            )
                          }
                          sx={{ flex: 1, minWidth: 60 }}
                          slotProps={{
                            input: {
                              style: { fontSize: "0.8rem" },
                            },
                          }}
                        />
                        <TextField
                          label="Disc/Unit"
                          size="small"
                          type="number"
                          value={cItem.discount === 0 ? "" : cItem.discount}
                          placeholder="0"
                          onChange={(e) =>
                            handleUpdateField(
                              cItem.itemId,
                              "discount",
                              e.target.value,
                            )
                          }
                          sx={{ flex: 1, minWidth: 60 }}
                          slotProps={{
                            input: {
                              style: { fontSize: "0.8rem" },
                            },
                          }}
                        />

                        {/* Quantity Controls Group */}
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ alignItems: "center" }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleUpdateField(
                                cItem.itemId,
                                "qty",
                                Number(cItem.qty) - 1,
                              )
                            }
                          >
                            <RemoveCircleIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            size="small"
                            type="number"
                            value={cItem.qty}
                            onChange={(e) =>
                              handleUpdateField(
                                cItem.itemId,
                                "qty",
                                e.target.value,
                              )
                            }
                            sx={{ width: 85 }}
                            slotProps={{
                              input: {
                                style: {
                                  textAlign: "center",
                                  fontSize: "0.8rem",
                                },
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleUpdateField(
                                cItem.itemId,
                                "qty",
                                Number(cItem.qty) + 1,
                              )
                            }
                          >
                            <AddCircleIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Box>
                ))
              )}
            </Stack>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              flexShrink: 0,
              mt: 2,
              pt: 2,
              borderTop: "1px dashed rgba(255,255,255,0.1)",
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="h6">Subtotal:</Typography>
              <Typography
                variant="h6"
                color="secondary"
                sx={{ fontWeight: "bold" }}
              >
                {formatCurrency(subtotal)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <AppButton
                variant="outlined"
                color="error"
                fullWidth
                pill
                onClick={() => setIsClearDialogOpen(true)}
              >
                Clear
              </AppButton>
              <AppButton
                variant="contained"
                color="primary"
                fullWidth
                pill
                disabled={cartItems.length === 0}
                onClick={onOpenPreview}
              >
                Preview
              </AppButton>
            </Stack>
          </Box>
        </Box>
      </Grid>

      <ConfirmDialog
        open={isClearDialogOpen}
        onClose={() => setIsClearDialogOpen(false)}
        onConfirm={() => {
          dispatch(resetReceipt());
          setIsClearDialogOpen(false);
        }}
        title="Clear Receipt"
        description="Are you sure you want to clear the cart?"
        confirmText="Clear All"
        severity="error"
      />
    </Grid>
  );
};

export default PosDesktopView;
